import axios from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { ApiError, type ApiResponse } from './types'
import { logger } from '@/lib/logger'
import { env } from '@/config/env'
import { IdentityAuthPath, readPostLogoutSkipAuthApexRedirectFlag } from '@/features/auth/lib/constants'
import { resolveOutboundSubdomain } from '@/features/auth/lib/request-subdomain'
import { useSessionStore } from '@/stores/useSessionStore'
import { API_REQUEST_TIMEOUT_MS } from './requestTimeout'

/** Lets the browser apply `Set-Cookie` from the refresh response before the next same-origin request. */
function yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => {
    queueMicrotask(() => {
      resolve()
    })
  })
}

let csrfToken: string | null = null

/** Single-flight refresh so parallel 401s share one POST /refresh. */
let refreshFlight: Promise<void> | null = null

export function setCsrfToken(token: string | null) {
  csrfToken = token
}

function authUrl(suffix: string): string {
  const base = env.VITE_API_BASE_URL.trim()
  return `${base}${suffix}`
}

/**
 * Calls identity `POST /api/v1/auth/refresh` with cookies so a new `access_token`
 * is issued. Uses standalone axios to avoid interceptor recursion.
 */
async function refreshSessionCookies(): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken
  }
  if (globalThis.window !== undefined) {
    const subdomain = resolveOutboundSubdomain(
      globalThis.window.location.hostname,
    )
    if (subdomain) {
      headers['x-subdomain'] = subdomain
    }
  }

  await axios.post(
    authUrl(IdentityAuthPath.REFRESH),
    {},
    { withCredentials: true, headers, timeout: API_REQUEST_TIMEOUT_MS },
  )
}

type MePayload = { csrf_token?: string }

/**
 * Re-fetch CSRF from Identity after refresh. The in-memory token (from last /me) can be
 * missing or stale after idle/HMR; mutating retries then fail without `X-CSRF-Token`.
 * Uses standalone axios (not apiClient) to avoid interceptor recursion.
 */
async function syncCsrfFromMe(): Promise<void> {
  const headers: Record<string, string> = {}
  if (globalThis.window !== undefined) {
    const subdomain = resolveOutboundSubdomain(
      globalThis.window.location.hostname,
    )
    if (subdomain) {
      headers['x-subdomain'] = subdomain
    }
  }

  const { data: envelope } = await axios.get<ApiResponse<MePayload>>(
    authUrl(IdentityAuthPath.ME),
    { withCredentials: true, headers, timeout: API_REQUEST_TIMEOUT_MS },
  )

  const csrf = envelope?.data?.csrf_token
  if (typeof csrf === 'string' && csrf.length > 0) {
    setCsrfToken(csrf)
  }
}

async function ensureSessionRefreshed(): Promise<void> {
  refreshFlight ??= (async () => {
    await refreshSessionCookies()
    await yieldToBrowser()
    await syncCsrfFromMe()
  })().finally(() => {
    refreshFlight = null
  })
  await refreshFlight
}

function sessionRefreshBlocked(): boolean {
  if (globalThis.window === undefined) return false
  const { explicitLogout, authTransitionInProgress } = useSessionStore.getState()
  return (
    explicitLogout ||
    authTransitionInProgress ||
    readPostLogoutSkipAuthApexRedirectFlag()
  )
}

function shouldAttemptRefresh(
  status: number,
  config: InternalAxiosRequestConfig | undefined,
): config is InternalAxiosRequestConfig {
  if (sessionRefreshBlocked()) return false
  if (status !== 401 || !config) return false
  if (config.__refreshRetried) return false
  const url = config.url ?? ''
  if (url.includes('/api/v1/auth/refresh')) return false
  return true
}

type ErrorBody = ApiResponse | undefined

function errorFromEnvelope(body: ErrorBody, status: number): ApiError {
  const nested =
    body?.error &&
    typeof body.error === 'object' &&
    body.error !== null &&
    'code' in body.error
      ? body.error
      : null
  const meta =
    body?.meta && typeof body.meta === 'object' && !Array.isArray(body.meta)
      ? (body.meta as Record<string, unknown>)
      : null

  return new ApiError(
    body?.message ??
      (typeof body?.error === 'string' ? body.error : 'Request failed'),
    status,
    nested?.code ?? 'unknown_error',
    nested?.details ?? null,
    meta,
  )
}

/** Browser: `location` on `window`; Node tests set `globalThis.window.location` only. */
function redirectToLogin(): void {
  const w = globalThis as Window & typeof globalThis
  const loc = w.window?.location ?? w.location
  if (loc) {
    loc.href = '/login'
  }
}

type AxiosErrorLike = {
  response?: { status?: number; data?: unknown }
  config?: InternalAxiosRequestConfig
  message?: string
}

async function handleResponseError(
  client: AxiosInstance,
  error: unknown,
): Promise<unknown> {
  if (error instanceof ApiError) {
    logger.error('API error', {
      err: error,
      status: error.status,
      code: error.code,
    })
    throw error
  }

  const err = error as AxiosErrorLike
  const status: number = err.response?.status ?? 0
  const failingConfig = err.config
  if (
    shouldAttemptRefresh(status, failingConfig) &&
    globalThis.window !== undefined
  ) {
    failingConfig.__refreshRetried = true
    try {
      await ensureSessionRefreshed()
      return client.request(failingConfig)
    } catch {
      logger.warn('session_refresh_failed', {
        url: failingConfig.url ?? 'unknown',
      })
    }
  }

  const body = err.response?.data as ErrorBody
  const url: string = failingConfig?.url ?? 'unknown'
  const method: string = failingConfig?.method?.toUpperCase() ?? 'UNKNOWN'

  if (body?.error) {
    const apiError = errorFromEnvelope(body, status)
    logger.error('API request failed', {
      err: apiError,
      status,
      code: apiError.code,
      method,
      url,
    })
    const skipRedirect = failingConfig?.skipUnauthorizedRedirect === true
    if (apiError.isUnauthorized && !skipRedirect) {
      redirectToLogin()
    }
    throw apiError
  }

  const skipRedirect = failingConfig?.skipUnauthorizedRedirect === true
  if (status === 401 && !skipRedirect) {
    redirectToLogin()
  }

  const networkError = new ApiError(
    body?.message ?? err.message ?? 'Network error',
    status,
    'network_error',
    null,
  )

  logger.error('Network / unexpected API error', {
    err: error,
    status,
    method,
    url,
  })

  throw networkError
}

export function attachInterceptors(client: AxiosInstance) {
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    if (
      csrfToken &&
      config.method &&
      !['get', 'head', 'options'].includes(config.method)
    ) {
      config.headers.set('X-CSRF-Token', csrfToken)
    }

    if (globalThis.window !== undefined) {
      const subdomain = resolveOutboundSubdomain(
        globalThis.window.location.hostname,
      )
      if (subdomain) {
        config.headers.set('x-subdomain', subdomain)
      }
    }

    return config
  })

  client.interceptors.response.use(
    (response) => {
      const body = response.data as ApiResponse
      if (body?.success === false && body.error) {
        throw errorFromEnvelope(body, response.status)
      }
      return response
    },
    (error) => handleResponseError(client, error),
  )
}
